import type { ColumnDef } from '@tanstack/react-table';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { NumberCell, Text } from '@feathr/components';

import type { IGoalRow } from './GoalsTable.utils';
import { goalTypeLabels } from './GoalsTable.utils';
import GoalsTargetCell from './GoalsTargetCell';

import * as styles from './GoalsTable.css';

// `size` is authoritative — GoalsTable uses a fixed table layout (styles.fixedLayout).
export function GoalsTableColumns(): Array<ColumnDef<IGoalRow>> {
  const { t } = useTranslation();

  const columns: Array<ColumnDef<IGoalRow>> = [
    {
      id: 'name',
      accessorKey: 'name',
      header: t('Goal'),
      enableSorting: true,
      sortDescFirst: false,
      size: 260,
      cell({ row }): JSX.Element {
        return (
          <div className={styles.truncate}>
            <Text.DefaultHeadingMedium>{row.original.name}</Text.DefaultHeadingMedium>
          </div>
        );
      },
    },
    {
      id: 'type',
      header: t('Type'),
      size: 170,
      cell({ row }): JSX.Element {
        const { type } = row.original;
        return <Text.DefaultRegular>{goalTypeLabels[type] || type}</Text.DefaultRegular>;
      },
    },
    {
      id: 'target',
      header: t('Target'),
      enableSorting: false,
      size: 260,
      cell({ row }): JSX.Element {
        const { target, type } = row.original;
        if (!target) {
          return <Text.DefaultRegular>-</Text.DefaultRegular>;
        }
        return (
          <div className={styles.truncate}>
            <GoalsTargetCell target={target} type={type} />
          </div>
        );
      },
    },
    {
      id: 'conversions',
      accessorKey: 'conversions',
      header: t('Conversions'),
      enableSorting: true,
      sortDescFirst: false,
      size: 120,
      meta: { align: 'right' },
      cell({ row }): JSX.Element {
        return <NumberCell format={'count'} value={row.original.conversions} />;
      },
    },
    {
      id: 'conversionValue',
      accessorKey: 'conversionValue',
      header: t('Conversion Value'),
      enableSorting: true,
      sortDescFirst: false,
      size: 150,
      meta: { align: 'right' },
      cell({ row }): JSX.Element {
        const { conversionValue, isConversionSegment } = row.original;
        if (conversionValue === 0 || isConversionSegment) {
          return <Text.DefaultRegular>-</Text.DefaultRegular>;
        }
        return <NumberCell format={'currency'} value={conversionValue} />;
      },
    },
  ];

  return columns;
}
